import data from './bankRatesData.json';

// ── Savings interpreters ──────────────────────────────────────────────────────

function flatTierSavings(config, amount) {
  if (config.maxCap && amount >= config.maxCap) return 'Contact Branch';
  for (const slab of config.slabs) {
    if (!slab.maxAmount || amount <= slab.maxAmount) return amount * slab.rate;
  }
  return amount * config.slabs[config.slabs.length - 1].rate;
}

function incrementalSavings(config, amount) {
  if (config.maxCap && amount >= config.maxCap) return 'Contact Branch';
  let interest = 0;
  let prev = 0;
  for (const slab of config.slabs) {
    if (!slab.maxAmount) {
      // last slab with no upper bound
      interest += (amount - prev) * slab.rate;
      return interest;
    }
    if (amount <= slab.maxAmount) {
      interest += (amount - prev) * slab.rate;
      return interest;
    }
    interest += (slab.maxAmount - prev) * slab.rate;
    prev = slab.maxAmount;
  }
  // above all defined slabs — use last rate
  const last = config.slabs[config.slabs.length - 1];
  interest += (amount - prev) * last.rate;
  return interest;
}

// IndusInd: incremental below 5Cr, flat above
function indusindSavings(amount) {
  if (amount <= 100000) return amount * 0.025;
  if (amount <= 2500000) return 100000 * 0.025 + (amount - 100000) * 0.03;
  if (amount <= 50000000) return 100000 * 0.025 + 2400000 * 0.03 + (amount - 2500000) * 0.04;
  if (amount <= 1000000000) return amount * 0.05;
  if (amount <= 1500000000) return amount * 0.0705;
  if (amount <= 14000000000) return amount * 0.07;
  return 'Contact Branch (MIBOR-linked above ₹1,400Cr)';
}

// Federal: flat up to 10Cr, incremental above, back to flat above 150Cr
function federalSavings(amount) {
  if (amount <= 100000000) return amount * 0.025;
  if (amount < 500000000) return 100000000 * 0.025 + (amount - 100000000) * 0.04;
  if (amount < 1500000000) return 100000000 * 0.025 + (amount - 100000000) * 0.055;
  return amount * 0.025;
}

function computeSavings(key, config, amount) {
  if (config.mode === 'custom') {
    if (key === 'Indusind') return indusindSavings(amount);
    if (key === 'Federal') return federalSavings(amount);
  }
  if (config.mode === 'incremental') return incrementalSavings(config, amount);
  return flatTierSavings(config, amount); // "flat_tier" and "flat" both use this
}

// ── FD interpreters ───────────────────────────────────────────────────────────

function computeFDSlabs(slabs, specialDays, amount, days) {
  // Special day exact matches take priority
  if (specialDays && specialDays.length) {
    const special = specialDays.find(s => s.days === days);
    if (special) return amount * special.rate;
  }
  for (const slab of slabs) {
    if (days <= slab.maxDays) return amount * slab.rate;
  }
  return 'Invalid tenure';
}

function computeFD(key, config, amount, days) {
  if (days < 7) return 'Invalid tenure';

  // Banks with amount tiers (Axis, BOI)
  if (config.tiers) {
    // Check for "Contact Branch" result tier first
    for (const tier of config.tiers) {
      if (tier.result) {
        if ((!tier.maxAmount || amount >= tier.minAmount) &&
            (!tier.maxAmount || amount <= tier.maxAmount)) {
          return tier.result;
        }
      }
    }
    const tier = config.tiers.find(t => {
      const aboveMin = !t.minAmount || amount >= t.minAmount;
      const belowMax = !t.maxAmount || amount <= t.maxAmount;
      return aboveMin && belowMax && !t.result;
    });
    if (tier) return computeFDSlabs(tier.slabs, config.specialDays, amount, days);
    if (config.maxAmountCap && amount >= config.maxAmountCap) return 'Contact Branch';
    return 'Contact Branch';
  }

  if (config.maxAmountCap && amount >= config.maxAmountCap) return 'Contact Branch';
  return computeFDSlabs(config.slabs, config.specialDays, amount, days);
}

// ── Build the BANKS export ────────────────────────────────────────────────────

const BANKS = {};

for (const [key, bank] of Object.entries(data)) {
  BANKS[key] = {
    savings: (amount) => computeSavings(key, bank.savings, amount),
    fd: (amount, days) => computeFD(key, bank.fd, amount, days),
    savingsDescription: bank.savingsDescription,
    fdDescription: bank.fdDescription,
    source: bank.source,
  };
}

export default BANKS;
